import { useRef, useState } from "react";
import { FileCheck2, FileX2, Upload, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { WORKS } from "../../data/site";
import { api, formatApiError } from "../../lib/api";

const CertRow = ({ w, cert, onChange }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("work_id", w.id);
    fd.append("file", file);
    try {
      await api.post("/work-certificates", fd);
      toast.success(`Certificate saved for ${w.title.slice(0, 40)}...`);
      onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove this certificate? Reviewers will no longer be able to download it.")) return;
    try {
      await api.delete(`/work-certificates/${cert.id}`);
      toast.success("Certificate removed");
      onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <li data-testid={`cert-file-row-${w.id}`} className="grid gap-3 border-b border-black/10 py-4 md:grid-cols-12 md:items-center">
      <div className="md:col-span-7">
        <p className="font-semibold">{w.title}</p>
        <p className="font-mono text-xs text-[var(--muted-ink)]">{w.yr} · {w.dept}</p>
      </div>
      <div className="text-sm md:col-span-3">
        {cert ? (
          <span className="flex items-center gap-1.5 text-[var(--forest)]"><FileCheck2 className="h-4 w-4 shrink-0" /> <span className="truncate">{cert.filename}</span></span>
        ) : (
          <span className="flex items-center gap-1.5 text-[var(--muted-ink)]"><FileX2 className="h-4 w-4" /> Not uploaded</span>
        )}
      </div>
      <div className="flex gap-2 md:col-span-2 md:justify-end">
        <input ref={ref} type="file" hidden accept="application/pdf,image/jpeg,image/png" onChange={(e) => upload(e.target.files?.[0])} data-testid={`cert-file-input-${w.id}`} />
        <button type="button" disabled={busy} onClick={() => ref.current?.click()} data-testid={`cert-file-upload-${w.id}`} className="inline-flex items-center gap-1.5 border border-[var(--ink)] px-3 py-1.5 text-xs font-semibold uppercase hover:bg-[var(--ink)] hover:text-white disabled:opacity-60">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} {cert ? "Replace" : "Upload"}
        </button>
        {cert && (
          <button type="button" onClick={remove} aria-label="Remove certificate" data-testid={`cert-file-delete-${w.id}`} className="border border-red-700 px-2 text-red-700 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
        )}
      </div>
    </li>
  );
};

export const CertificateManager = () => {
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["work-certificates"], queryFn: async () => (await api.get("/work-certificates")).data });
  const byWork = Object.fromEntries(data.map((c) => [c.work_id, c]));
  const refresh = () => qc.invalidateQueries({ queryKey: ["work-certificates"] });

  return (
    <section data-testid="certificate-manager">
      <p className="max-w-3xl text-[var(--muted-ink)]">
        Upload the work done certificate (PDF, JPG or PNG) once per work. When you mark a request as sent, the signed-in reviewer can download it from My account.
        {" "}{data.length} of {WORKS.length} works {data.length === 1 ? "has" : "have"} a certificate on file.
      </p>
      {isLoading ? <Loader2 className="mt-8 h-6 w-6 animate-spin" /> : (
        <ul className="mt-8 border-t-2 border-[var(--ink)]">
          {WORKS.map((w) => <CertRow key={w.id} w={w} cert={byWork[w.id]} onChange={refresh} />)}
        </ul>
      )}
    </section>
  );
};
